import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';

/**
 * The first page of a PDF drawn onto a canvas with pdf.js, so it shows the same everywhere:
 * Android's browsers can't show a PDF inside a page (they offer an Open button instead).
 * pdf.js is loaded only when a preview is first shown.
 */
export function PdfPreview({ url, title, height }: { url: string; title: string; height: string }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    void (async () => {
      try {
        const [pdfjs, worker] = await Promise.all([import('pdfjs-dist'), import('pdfjs-dist/build/pdf.worker.min.mjs?url')]);
        // ?v=2: browsers that cached the worker when it was served with the wrong type fetch it again.
        pdfjs.GlobalWorkerOptions.workerSrc = `${worker.default}?v=2`;
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) throw new Error(String(res.status));
        const doc = await pdfjs.getDocument({ data: new Uint8Array(await res.arrayBuffer()) }).promise;
        const page = await doc.getPage(1);
        const c = canvas.current;
        if (cancelled || !c) return;
        // Sharp on high-density screens: draw at the displayed height times the pixel ratio.
        const base = page.getViewport({ scale: 1 });
        const scale = (Math.max(c.clientHeight, 300) * Math.min(window.devicePixelRatio || 1, 3)) / base.height;
        const viewport = page.getViewport({ scale });
        c.width = Math.round(viewport.width);
        c.height = Math.round(viewport.height);
        await page.render({ canvasContext: c.getContext('2d')!, viewport }).promise;
        void doc.destroy();
        if (!cancelled) setState('ready');
      } catch (e) {
        console.error('Sticker preview:', e);
        if (!cancelled) setState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url]);

  return (
    <Box sx={{ position: 'relative', height, maxWidth: '100%', aspectRatio: '86 / 186', display: 'grid', placeItems: 'center' }}>
      <Box component="canvas" ref={canvas} role="img" aria-label={title} sx={{ display: 'block', height: '100%', width: 'auto', maxWidth: '100%', visibility: state === 'ready' ? 'visible' : 'hidden', boxShadow: 3 }} />
      {state !== 'ready' && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
          {state === 'loading' ? <CircularProgress size={28} sx={{ color: '#888' }} /> : <Typography sx={{ color: '#aaa' }}>Couldn't show the preview.</Typography>}
        </Box>
      )}
    </Box>
  );
}
